import { Card, CardContent, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow } from "@mui/material";
import React, { useCallback, useState } from "react";
import { IMaterialAudit, IMaterialAuditProps, IMaterialAuditResult } from "../../interfaces";
import { FormSectionHeader } from "../Shared/FormSectionHeader";
import { MaterialAuditTableRow } from "./MaterialAuditTableRow";
import { useMaterialAuditGroup } from "./useMaterialAuditGroup";

export const MaterialAuditGroupForm: React.FC<IMaterialAuditProps> = ({
    materialAudit,
    materialStates,
    updateMaterialAudit,
    reloadMaterial,
    readonly,
    loading
}) => {
    const { modelArray, onUpdateMaterialAuditResult } = useMaterialAuditGroup(materialAudit?.materialAuditResults, readonly);
    const [groupPagination, setGroupPagination] = useState<{ [key: string]: { page: number; rowsPerPage: number } }>({});

    const handleUpdate = useCallback(
        (updatedMaterialAuditResult: IMaterialAuditResult, removeResult = false) => {
            const updatedResults = onUpdateMaterialAuditResult(updatedMaterialAuditResult, removeResult);

            updateMaterialAudit({
                ...materialAudit,
                materialAuditResults: updatedResults,
            } as IMaterialAudit);
        },
        [materialAudit, onUpdateMaterialAuditResult, updateMaterialAudit]
    );

    const onRemoveMaterialAuditResult = useCallback(
        (materialAuditResult: IMaterialAuditResult) => {
            handleUpdate(materialAuditResult, true);
        },
        [handleUpdate]
    );

    const onUpdateResult = useCallback(
        (materialAuditResult: IMaterialAuditResult) => {
            handleUpdate(materialAuditResult);
        },
        [handleUpdate]
    );

    const getGroupKey = (model: IMaterialAuditResult[] | undefined, index: number) =>
        (model && model[0].material.site?.id) ?? `Site Unknown ${index}`;

    const getGroupLabel = (model: IMaterialAuditResult[] | undefined, index: number) =>
        (model && model[0].material.site?.value) ?? `Site Unknown ${index}`;

    const getPagination = (key: string, rowCount: number) => {
        const pagination = groupPagination[key] ?? { page: 0, rowsPerPage: 50 };
        const maxPage = Math.max(Math.ceil(rowCount / pagination.rowsPerPage) - 1, 0);

        return {
            ...pagination,
            page: Math.min(pagination.page, maxPage)
        };
    };

    const setGroupPage = (key: string, page: number) => {
        setGroupPagination(previous => ({
            ...previous,
            [key]: {
                ...(previous[key] ?? { rowsPerPage: 50 }),
                page
            }
        }));
    };

    const setGroupRowsPerPage = (key: string, rowsPerPage: number) => {
        setGroupPagination(previous => ({
            ...previous,
            [key]: {
                page: 0,
                rowsPerPage
            }
        }));
    };

    return (
        <>
            {modelArray.map((model, i) => {
                const groupKey = getGroupKey(model, i);
                const pagination = getPagination(groupKey, model?.length ?? 0);
                const pageRows = model?.slice(
                    pagination.page * pagination.rowsPerPage,
                    pagination.page * pagination.rowsPerPage + pagination.rowsPerPage);

                return (
                    <Card sx={{ my: 3 }} key={groupKey}>
                        <FormSectionHeader label={getGroupLabel(model, i)} saving={loading} />
                        <CardContent>
                            <Grid container>
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Vehicle Class</TableCell>
                                                <TableCell>Location</TableCell>
                                                <TableCell>Part Identification</TableCell>
                                                <TableCell>Description</TableCell>
                                                <TableCell>Qty</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell>Rationale</TableCell>
                                                <TableCell>Retention Decision</TableCell>
                                                <TableCell>Audit Comments</TableCell>
                                                <TableCell />
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {pageRows &&
                                                pageRows.map((mar) => (
                                                    <MaterialAuditTableRow
                                                        key={mar.material?.materialId}
                                                        materialAuditResult={mar}
                                                        materialStates={materialStates}
                                                        onRemove={() => onRemoveMaterialAuditResult(mar)}
                                                        onUpdate={onUpdateResult}
                                                        onReloadMaterial={reloadMaterial}
                                                        readonly={readonly}
                                                    />
                                                ))}
                                        </TableBody>
                                    </Table>
                                    <TablePagination
                                        component="div"
                                        count={model?.length ?? 0}
                                        page={pagination.page}
                                        rowsPerPage={pagination.rowsPerPage}
                                        rowsPerPageOptions={[25, 50, 100, 250]}
                                        onPageChange={(_, page) => setGroupPage(groupKey, page)}
                                        onRowsPerPageChange={(event) => setGroupRowsPerPage(groupKey, parseInt(event.target.value, 10))}
                                    />
                                </TableContainer>
                            </Grid>
                        </CardContent>
                    </Card>
                );
            })}
        </>
    );
};
